import {
  deleteV1CompaniesCompanyIdSitesId,
  getV1CompaniesCompanyIdOrdersOrderIdDefaultSites,
  getV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointType,
  GetV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeData,
  getV1CompaniesCompanyIdSites,
  GetV1CompaniesCompanyIdSitesData,
  getV1CompaniesCompanyIdSitesDispatchIndex,
  GetV1CompaniesCompanyIdSitesDispatchIndexData,
  getV1CompaniesCompanyIdSitesDispatchTypeahead,
  GetV1CompaniesCompanyIdSitesDispatchTypeaheadData,
  getV1CompaniesCompanyIdSitesId,
  getV1CompaniesCompanyIdSitesSiteIdContacts,
  GetV1CompaniesCompanyIdSitesSiteIdContactsData,
  getV1CompaniesCompanyIdSiteTypeahead,
  GetV1CompaniesCompanyIdSiteTypeaheadData,
  NextBillionGeofence_Create,
  patchV1CompaniesCompanyIdSitesSiteIdAdditionalGeofencesBulkAssign,
  postV1CompaniesCompanyIdSites,
  putV1CompaniesCompanyIdOrdersOrderIdDefaultSitesWaypointTypeSiteId,
  putV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeSiteId,
  putV1CompaniesCompanyIdSitesSiteIdActivate,
  putV1CompaniesCompanyIdSitesSiteIdDeactivate,
  Site_Read,
  SiteState,
  SiteType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback } from 'react';

import { ACTIVE_JOB_STATES } from '~components/Maps/v2/constants';
import { DEFAULT_API_PAGE_SIZE, MAX_API_PAGE_SIZE } from '~constants/consts';
import { SiteWithAdditionalGeoFences } from '~pages/Settings/Administration/Sites/GeoFenceForm';
import { Filters } from '~pages/Settings/Administration/Sites/hooks/useSitesFilters';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ContactItem } from '../useContact/models/ContactItem';
import { Geofence, Site, SiteTypeahead } from './models';

interface ToggleProps {
  companyId?: string;
  id: string;
  isActive: boolean;
}
interface GetCompanySitesProps {
  companyId: string;
  cursor?: string;
  link?: PaginationLink;
  searchQuery?: string;
  filters?: Filters;
  limit?: number;
  signal?: AbortSignal;
}
interface GetCompanySitesTypeaheadProps {
  companyId: string;
  query?: string;
  cursor?: string;
  limit?: number;
  onlyActiveSites?: boolean;
  siteTypes?: SiteType[];
}

export interface GetCompanySitesDispatchTypeaheadProps {
  companyId: string;
  query?: string;
  cursor?: string;
  limit?: number;
  siteType?: 'pickup' | 'drop_off';
}

interface GetCompanySitesDispatchIndexProps {
  companyId: string;
  cursor?: string;
  limit?: number;
  siteType?: 'pickup' | 'drop_off';
}

interface SiteToProjectDefaultSitesParams {
  companyId?: string;
  projectId: string;
  siteId: string;
  waypointType: DefaultSitesWaypointType;
}
interface GetProjectDefaultSitesParams {
  companyId?: string;
  projectId: string;
  waypointType: DefaultSitesWaypointType;
}
interface SiteToOrderDefaultSitesParams {
  companyId?: string;
  orderId: string;
  siteId: string;
  waypointType: DefaultSitesWaypointType;
}
interface GetOrderDefaultSitesParams {
  companyId?: string;
  orderId: string;
  waypointType: DefaultSitesWaypointType;
}

type CompanySitesQuery = NonNullable<GetV1CompaniesCompanyIdSitesData['query']>;
export type DefaultSitesWaypointType =
  GetV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeData['path']['waypoint-type'];

export const useSites = () => {
  const { companyAssetsStore, ordersDispatchStore, liveMapStoreNew, userStore } =
    useStores();

  const companyId = getEffectiveUserCompanyId(userStore);

  const getSiteById = async (companyId: string, id: string) => {
    try {
      const resp = await getV1CompaniesCompanyIdSitesId({
        path: { 'company-id': companyId, id: id },
      });

      const site = Site.parse(resp.data.data);
      return site;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_fetch_site'),
      );
      throw error;
    }
  };

  const getCompanySites = useCallback(
    async ({
      companyId,
      cursor,
      filters,
      limit = MAX_API_PAGE_SIZE,
      link,
      searchQuery,
      signal,
    }: GetCompanySitesProps) => {
      try {
        const params: CompanySitesQuery = {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        };
        if (searchQuery) {
          params['search[datagrid]'] = searchQuery;
        }

        if (filters?.siteTypes?.length) {
          params['filter[site_types]'] = filters.siteTypes;
        }

        if (filters?.departments?.length) {
          params['filter[department_ids]'] = filters.departments;
        }

        const vis = filters?.visibility;
        if (vis) {
          const includesInternal = vis.includes('internal');
          const includesExternal = vis.includes('external');

          // If both are selected/not selected, we don't need to filter on shared
          if (includesInternal !== includesExternal) {
            params['filter[shared]'] = includesExternal;
          }
        }

        const response = await getV1CompaniesCompanyIdSites({
          path: { 'company-id': companyId },
          query: params,
          signal,
        });

        return {
          data: response.data.data.map(Site.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        // a superseded request isn't a failure worth a toast
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(error, t('error_messages.sites.failed_to_fetch'));
        throw error;
      }
    },
    [],
  );

  const getCompanySitesTypeahead = async ({
    companyId,
    query,
    cursor,
    limit,
    onlyActiveSites,
    siteTypes,
  }: GetCompanySitesTypeaheadProps) => {
    const params: GetV1CompaniesCompanyIdSiteTypeaheadData['query'] = {
      'page[limit]': limit ?? DEFAULT_API_PAGE_SIZE,
    };

    if (query) {
      params['search[query]'] = query;
    }

    if (cursor) {
      params['page[after]'] = cursor;
    }

    if (onlyActiveSites) {
      params['filter[states]'] = [SiteState.ACTIVE];
    }

    if (siteTypes?.length) {
      params['filter[site_types]'] = siteTypes;
    }

    try {
      const response = await getV1CompaniesCompanyIdSiteTypeahead({
        path: { 'company-id': companyId },
        query: params,
      });
      const list = response.data.data.map((site) => Site.parse(site as Site_Read));
      const pagination = extractPagination(response);

      return { data: list, pagination };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.sites.failed_to_fetch'));
      throw error;
    }
  };

  const getCompanySitesDispatchTypeahead = async ({
    companyId,
    query,
    cursor,
    limit,
    siteType,
  }: GetCompanySitesDispatchTypeaheadProps) => {
    const { filters } = ordersDispatchStore;

    const params: GetV1CompaniesCompanyIdSitesDispatchTypeaheadData['query'] = {
      'page[limit]': limit,
    };

    if (query) {
      params['facet[search][query]'] = query;
    }
    if (siteType) {
      params['facet[filter][waypoint][type]'] = siteType;
    }

    if (cursor) {
      params['page[after]'] = cursor;
    }

    if (filters?.search?.trim().length) {
      params['search[job][datagrid]'] = filters.search.trim();
    }

    if (filters?.customerAccountIds?.length) {
      params['filter[job][customer_account_ids]'] = filters.customerAccountIds;
    }

    if (filters?.dispatchNumberIds?.length) {
      params['filter[dispatch_number_ids]'] = filters.dispatchNumberIds;
    }

    if (filters?.driverIds?.length) {
      params['filter[job][driver_ids]'] = filters.driverIds;
    }

    if (filters?.projectsExternalIds?.length) {
      params['filter[job][external_ids]'] = filters.projectsExternalIds;
    }

    if (filters?.projectIds?.length) {
      params['filter[job][project_ids]'] = filters.projectIds;
    }

    if (filters?.vendorAccountIds?.length) {
      params['filter[job][vendor_account_ids]'] = filters.vendorAccountIds;
    }

    if (filters?.startDate?.length) {
      params['filter[job][start_date]'] = dayjs
        .tz(filters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (filters?.endDate?.length) {
      params['filter[job][end_date]'] = dayjs
        .tz(filters.endDate)
        .endOf('day')
        .toISOString();
    }

    if (filters?.jobStates?.length) {
      params['filter[job][states]'] = filters.jobStates;
    }

    if (filters?.siteTypes?.length) {
      params['filter[job][site_types]'] = filters.siteTypes;
    }

    try {
      const response = await getV1CompaniesCompanyIdSitesDispatchTypeahead({
        path: { 'company-id': companyId },
        query: params,
      });
      const sites = response.data.data.map((site) => SiteTypeahead.parse(site));
      const pagination = extractPagination(response);

      return { data: sites, pagination };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.sites.failed_to_fetch'));
      throw error;
    }
  };

  const getCompanySitesDispatchIndex = async ({
    companyId,
    limit,
    cursor,
    siteType,
  }: GetCompanySitesDispatchIndexProps) => {
    const { filters } = liveMapStoreNew;

    const params: GetV1CompaniesCompanyIdSitesDispatchIndexData['query'] = {
      'page[limit]': limit || MAX_API_PAGE_SIZE,
      'filter[job][states]': ACTIVE_JOB_STATES,
    };

    if (siteType) {
      params['facet[filter][waypoint][type]'] = siteType;
    }

    if (filters?.startDate?.length) {
      params['filter[job][start_date]'] = dayjs
        .tz(filters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (filters?.endDate?.length) {
      params['filter[job][end_date]'] = dayjs
        .tz(filters.endDate)
        .endOf('day')
        .toISOString();
    }

    if (cursor) {
      params['page[after]'] = cursor;
    }

    try {
      const response = await getV1CompaniesCompanyIdSitesDispatchIndex({
        path: { 'company-id': companyId },
        query: params,
      });
      const sites = response.data.data.map((site) => Site.parse(site));
      const pagination = extractPagination(response);

      return { data: sites, pagination };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.sites.failed_to_fetch'));
      throw error;
    }
  };

  const createNewSite = async (data: SiteWithAdditionalGeoFences) => {
    const siteCompanyId = data.company?.id || companyId;
    const additionalGeofences = data.additionalGeoFencesDetails
      ?.map(
        (geofence) =>
          Geofence.deparseAdditionalGeoFence(geofence) as
            | NextBillionGeofence_Create
            | undefined,
      )
      .filter((geofence): geofence is NextBillionGeofence_Create => Boolean(geofence));
    let site: Site | undefined;
    try {
      const response = await postV1CompaniesCompanyIdSites({
        path: { 'company-id': siteCompanyId },
        body: Site.deparseCreate(data),
      });
      site = Site.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_create'),
        [422],
      );
      throw error;
    }

    if (site && additionalGeofences?.length) {
      await bulkAssignAdditionalGeoFencesToSite(
        site.id,
        additionalGeofences,
        true,
        siteCompanyId,
      );
    }

    return site;
  };

  const toggleSiteStatus = async ({
    isActive,
    id,
    companyId: siteCompanyId,
  }: ToggleProps) => {
    try {
      const path = { 'company-id': siteCompanyId || companyId, 'site-id': id };
      const response = isActive
        ? await putV1CompaniesCompanyIdSitesSiteIdDeactivate({ path })
        : await putV1CompaniesCompanyIdSitesSiteIdActivate({ path });
      const site = Site.parse(response.data.data);
      return site;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_toggle_status', {
          status: isActive ? 'deactivate' : 'activate',
        }),
      );
      throw error;
    }
  };

  const removeSite = async (id: string, siteCompanyId?: string) => {
    try {
      await deleteV1CompaniesCompanyIdSitesId({
        path: { 'company-id': siteCompanyId || companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.sites.failed_to_delete'));
      throw error;
    }
  };
  const getProjectDefaultSites = async ({
    projectId,
    waypointType,
    companyId: projectCompanyId,
  }: GetProjectDefaultSitesParams) => {
    try {
      const response =
        await getV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointType({
          path: {
            'company-id': projectCompanyId || companyId,
            'project-id': projectId,
            'waypoint-type': waypointType,
          },
        });

      return response.data.data.map(Site.parseNested);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_fetch_project_default_sites'),
      );
      throw error;
    }
  };
  const addSiteToProjectDefaultSites = async ({
    projectId,
    siteId,
    waypointType,
    companyId: projectCompanyId,
  }: SiteToProjectDefaultSitesParams) => {
    try {
      const response =
        await putV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeSiteId({
          path: {
            'company-id': projectCompanyId || companyId,
            'project-id': projectId,
            'waypoint-type': waypointType,
            'site-id': siteId,
          },
        });

      return Site.parseNested(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_add_to_project_default_sites'),
      );
      throw error;
    }
  };
  const getOrderDefaultSites = async ({
    orderId,
    waypointType,
    companyId: orderCompanyId,
  }: GetOrderDefaultSitesParams) => {
    try {
      const response = await getV1CompaniesCompanyIdOrdersOrderIdDefaultSites({
        path: {
          'company-id': orderCompanyId || companyId,
          'order-id': orderId,
          'waypoint-type': waypointType,
        },
      });

      return response.data.data.map(Site.parseNested);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_fetch_default_sites'),
      );
      throw error;
    }
  };

  const deleteRecentlyCreatedSite = async (siteId: string, siteCompanyId?: string) => {
    await toggleSiteStatus({ id: siteId, isActive: true, companyId: siteCompanyId });
    await removeSite(siteId, siteCompanyId);
  };

  const addSiteToOrderDefaultSites = async ({
    orderId,
    siteId,
    waypointType,
    companyId: orderCompanyId,
  }: SiteToOrderDefaultSitesParams) => {
    try {
      const response =
        await putV1CompaniesCompanyIdOrdersOrderIdDefaultSitesWaypointTypeSiteId({
          path: {
            'company-id': orderCompanyId || companyId,
            'order-id': orderId,
            'waypoint-type': waypointType,
            'site-id': siteId,
          },
        });

      return Site.parseNested(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_add_to_order_default_sites'),
      );
      throw error;
    }
  };
  const bulkAssignAdditionalGeoFencesToSite = async (
    siteId: string,
    additionalGeofences: NextBillionGeofence_Create[],
    creation = false,
    siteCompanyId?: string,
  ) => {
    try {
      const response =
        await patchV1CompaniesCompanyIdSitesSiteIdAdditionalGeofencesBulkAssign({
          path: { 'company-id': siteCompanyId || companyId, 'site-id': siteId },
          body: { additional_geofences: additionalGeofences },
        });
      const parsedGeofences = response.data.data.map(Geofence.parse);
      companyAssetsStore.setActiveSiteAdditionalGeoFences(parsedGeofences);
      return parsedGeofences;
    } catch (error) {
      if (creation) {
        await deleteRecentlyCreatedSite(siteId, siteCompanyId);
        connection.handleRequestError(error, t('error_messages.sites.failed_to_create'));
      }

      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_assign_additional_geofences'),
      );
      throw error;
    }
  };

  interface GetSiteContactsProps {
    companyId: string;
    siteId: string;
    limit?: number;
    cursor?: string;
  }

  const getSiteContacts = async (options: GetSiteContactsProps) => {
    const limit = options.limit ?? 10;
    const query: GetV1CompaniesCompanyIdSitesSiteIdContactsData['query'] = {
      'page[limit]': limit,
    };

    if (options?.cursor) {
      query['page[after]'] = options.cursor;
    }

    try {
      const response = await getV1CompaniesCompanyIdSitesSiteIdContacts({
        path: { 'company-id': options.companyId, 'site-id': options.siteId },
        query,
      });
      return response.data.data.map(ContactItem.parse);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.sites.failed_to_fetch'));
      throw error;
    }
  };

  return {
    addSiteToOrderDefaultSites,
    addSiteToProjectDefaultSites,
    createNewSite,
    getCompanySites,
    getCompanySitesTypeahead,
    getCompanySitesDispatchIndex,
    getProjectDefaultSites,
    getOrderDefaultSites,
    getSiteById,
    removeSite,
    toggleSiteStatus,
    getCompanySitesDispatchTypeahead,
    getSiteContacts,
  };
};
